import axios from 'axios';
import Alert from '@mui/lab/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Loader from 'react-loader-spinner';
import { useHistory } from 'react-router';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import React, { useState, useEffect, useCallback } from 'react';
import DialogContentText from '@mui/material/DialogContentText';

import { classes } from '../styles';
import { formatDate } from '../../../utils/moment.util';
import DashboardNavBar from '../../../components/Dashboard/Common/Navbar';
import DashboardSideMenu from '../../../components/Dashboard/Common/SidebarMenu';

const baseUrl = process.env.REACT_APP_BASE_URL;
const token = localStorage.getItem('lhcfToken');

const DashboardSponsoredKidPage = () => {
  const history = useHistory();
  const { kidId } = useParams();

  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [sponsoredKid, setSponsoredKid] = useState({
    firstName: '',
    lastName: '',
    active: false,
    country: '',
    dateOfBirth: '',
    image: '',
    registered: '',
  });
  const [snackbarState, setSnackbarState] = useState({
    errorOpen: false,
    successOpen: false,
    vertical: 'center',
    horizontal: 'center',
  });
  const { successOpen, errorOpen, vertical, horizontal } = snackbarState;

  const breadcrumbs = [
    <Link
      underline="hover"
      key="1"
      color="inherit"
      href="/dashboard"
      onClick={() => history.push('/dashboard')}
    >
      Dashboard
    </Link>,
    <Link
      underline="hover"
      key="2"
      color="inherit"
      href="/dashboard/sponsored-kids/"
      onClick={() => history.push('/dashboard/sponsored-kids/')}
    >
      Sponsored Kids
    </Link>,
    <Typography key="3" color="text.primary">
      Single Sponsored Kid
    </Typography>,
  ];

  const handleClose = () => {
    setSnackbarState({
      ...snackbarState,
      errorOpen: false,
      successOpen: false,
    });
  };

  const fetchSponsoredKid = useCallback((kidId) => {
    axios
      .get(`${baseUrl}/sponsored-kid/${kidId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data: { data } }) => {
        setSponsoredKid(data);
        setLoading(false);
      })
      .catch((err) => {
        setLoadingError(true);
        setLoading(false);
      });
  }, []);

  const handleDeletion = (kidId) => {
    setDialogOpen(false);
    setDeletionLoading(true);

    axios
      .delete(`${baseUrl}/sponsored-kid/${kidId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        setDeletionLoading(false);
        setSnackbarState({ ...snackbarState, successOpen: true });
        setTimeout(() => {
          history.push('/dashboard/sponsored-kids/');
        }, 3000);
      })
      .catch((err) => {
        console.log(err);
        setDeletionLoading(false);
        setSnackbarState({ ...snackbarState, errorOpen: true });
      });
  };

  useEffect(() => {
    fetchSponsoredKid(kidId);
  }, [fetchSponsoredKid, kidId]);

  return (
    <Box sx={classes.container}>
      <DashboardNavBar />
      <DashboardSideMenu />
      {/* PAGE */}
      <Box sx={classes.pageContainer}>
        <Snackbar
          open={errorOpen}
          autoHideDuration={10000}
          onClose={handleClose}
          anchorOrigin={{ vertical, horizontal }}
        >
          <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
            Could not delete the sponsored kid. Try again.
          </Alert>
        </Snackbar>
        <Snackbar
          open={successOpen}
          autoHideDuration={10000}
          onClose={handleClose}
          anchorOrigin={{ vertical, horizontal }}
        >
          <Alert
            onClose={handleClose}
            severity="success"
            sx={{ width: '100%' }}
          >
            Sponsored kid deleted successfully!
          </Alert>
        </Snackbar>
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle color="error" id="alert-dialog-title">
            {'DELETION CONFIRMATION'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Do you really want to remove {sponsoredKid.firstName}{' '}
              {sponsoredKid.lastName} from the sponsored kids?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              color="error"
              onClick={() => handleDeletion(kidId)}
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        {loading ? (
          <Box sx={classes.commonMargin}>
            <Loader type="Oval" color="#B40404" height={80} width={80} />
          </Box>
        ) : loadingError ? (
          <Box sx={classes.commonMargin}>
            <Typography variant="subtitle1" component="div">
              Could not fetch the sponsored kid. Refresh or check back later.
            </Typography>
          </Box>
        ) : (
          <>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {breadcrumbs}
            </Breadcrumbs>
            <Box sx={classes.paperContainer}>
              <Paper elevation={4} variant="outlined" square>
                <img src={sponsoredKid.image} width="400" alt="kid" />

                <br />
                <br />

                <TextField
                  id="filled-basic"
                  label="First Name"
                  name="firstName"
                  variant="filled"
                  value={sponsoredKid.firstName}
                  sx={classes.commonTextFieldWidth}
                  disabled
                />

                <br />
                <br />

                <TextField
                  id="filled-basic"
                  label="Last Name"
                  name="lastName"
                  variant="filled"
                  value={sponsoredKid.lastName}
                  sx={classes.commonTextFieldWidth}
                  disabled
                />

                {sponsoredKid.fatherName && (
                  <>
                    <br />
                    <br />

                    <TextField
                      id="filled-basic"
                      label="Father Name"
                      name="fatherName"
                      variant="filled"
                      value={sponsoredKid.fatherName}
                      sx={classes.commonTextFieldWidth}
                      disabled
                    />
                  </>
                )}

                {sponsoredKid.motherName && (
                  <>
                    <br />
                    <br />
                    <TextField
                      id="filled-basic"
                      label="Mother Name"
                      name="motherName"
                      variant="filled"
                      value={sponsoredKid.motherName}
                      sx={classes.commonTextFieldWidth}
                      disabled
                    />
                  </>
                )}

                <br />
                <br />

                <TextField
                  id="filled-basic"
                  label="Country"
                  name="country"
                  variant="filled"
                  value={sponsoredKid.country}
                  sx={classes.commonTextFieldWidth}
                  disabled
                />

                {sponsoredKid.city && (
                  <>
                    <br />
                    <br />
                    <TextField
                      id="filled-basic"
                      label="City"
                      name="city"
                      variant="filled"
                      value={sponsoredKid.city}
                      sx={classes.commonTextFieldWidth}
                      disabled
                    />
                  </>
                )}

                <br />
                <br />

                <TextField
                  id="filled-basic"
                  label="Date of Birth"
                  name="dateOfBirth"
                  variant="filled"
                  value={formatDate(sponsoredKid.dateOfBirth, 'DD-MM-YYYY')}
                  sx={classes.commonTextFieldWidth}
                  disabled
                />

                <br />
                <br />

                <TextField
                  id="filled-basic"
                  label="Registered"
                  name="registered"
                  variant="filled"
                  value={formatDate(sponsoredKid.createdAt, 'DD-MM-YYYY')}
                  sx={classes.commonTextFieldWidth}
                  disabled
                />
                <br />
                <br />

                <Box sx={classes.buttonsContainer}>
                  {deletionLoading ? (
                    <Button variant="contained" color="primary" disabled>
                      <Loader
                        type="Oval"
                        color="#1976d2"
                        height={30}
                        width={50}
                      />
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={classes.buttonMargin}
                        onClick={() =>
                          history.push(
                            `/dashboard/sponsored-kids/${kidId}/edit`
                          )
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDialogOpen(true)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Box>
              </Paper>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default DashboardSponsoredKidPage;
